import { Component, Input, OnChanges, OnDestroy } from '@angular/core';
import {
  AnalyticsService,
  FrkBreakpoint,
  ResponsiveService,
} from '@frk/eds-components';
import {
  documentNameGenerator,
  fundNameGenerator,
  getDescription,
  nameGenerator,
} from '@literature/literature-utils';
import { LiteratureDataService } from '@literature/services/literature-data.service';
import { StorageService } from '@services';
import { IUserProfile, UserRole } from '@services/profile.interface';
import { ProfileService } from '@services/profile.service';
import { TranslateService } from '@shared/translate/translate.service';
import { LiteratureDocumentListingData } from '@types';
import { SH } from '@utils/app.constants';
import get from 'lodash/get';
import { Observable, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { createNavigationString } from '@utils/text/string-utils';

@Component({
  selector: 'ft-fund-name-cell-renderer',
  templateUrl: './fund-name-cell-renderer.component.html',
  styleUrls: ['./fund-name-cell-renderer.component.scss'],
})
export class FundNameCellRendererComponent implements OnChanges, OnDestroy {
  @Input() cellRendererData: LiteratureDocumentListingData;
  isSiteIntl: boolean;
  fundName: string;
  fundLink: string;
  clientUse: string;
  loginRequired: boolean;
  isLoggedIn: boolean;
  isUpdateNotificationEnabled: boolean;
  updateNotificationMessage: string;
  showUpdateNotification = false;
  isShareHolderProfile = false;
  showOnlyDocumentName: boolean;
  showOnlyTitle: boolean;
  isColumnLayout: boolean;
  componentName: string;
  isCanada: boolean;
  public profileData: IUserProfile;
  public params: LiteratureDocumentListingData;
  public loginSubscription: Subscription;
  private unsubscribe$: Subject<void> = new Subject<void>();
  public details: string;
  public isMobile$: Observable<boolean>;
  public isLargeDesktop: boolean;
  public isShowDownload: boolean;

  constructor(
    private translateService: TranslateService,
    private profileService: ProfileService,
    private literatureDataService: LiteratureDataService,
    private storageService: StorageService,
    private responsiveService: ResponsiveService,
    private analyticsService: AnalyticsService
  ) {
    this.isMobile$ = this.responsiveService.isMobile$();
    this.responsiveService
      .getBreakpointObs$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((bp: FrkBreakpoint) => {
        if ([FrkBreakpoint.LargeDesktop, FrkBreakpoint.Desktop].includes(bp)) {
          this.isLargeDesktop = true;
        }
      });
  }

  ngOnChanges(): void {
    if (this.cellRendererData) {
      this.agInit(this.cellRendererData);
    }
  }
  agInit(params: any) {
    this.params = params.data;
    this.isSiteIntl = get(params, 'isSiteIntl', true);
    this.isCanada = get(params, 'isCanada', false);
    const litDetailUrl = get(params, 'litDetailUrl');
    this.showOnlyDocumentName = get(params, 'showOnlyDocumentName', false);
    this.showOnlyTitle = get(params, 'showOnlyTitle', false);
    this.isColumnLayout = get(params, 'isColumnLayout', false);
    this.componentName = get(params, 'componentName', 'default');
    this.loginRequired = this.params.literatureData.loginRequired;
    this.literatureDataService
      .getProfileData$()
      ?.pipe(takeUntil(this.unsubscribe$))
      .subscribe((data: IUserProfile) => {
        this.profileData = data;
        this.isLoggedIn = this.profileData?.isLoggedIn;
        this.isShareHolderProfile =
          UserRole[this.profileData?.profileInfo?.role] ===
          SH.toLocaleLowerCase();
      });
    if (this.params.literatureData.clientUse?.trim() !== '') {
      this.clientUse = this.params.literatureData.clientUse;
    }
    if (this.showOnlyDocumentName) {
      this.fundName = documentNameGenerator(
        params.data,
        this.translateService,
        this.showOnlyTitle
      );
      if (!this.isSiteIntl) {
        this.fundLink = this.getDownloadLinkWithPreview(params?.data);
      }
    } else {
      if (this.isSiteIntl) {
        this.fundName = nameGenerator(
          params.data,
          this.translateService,
          this.isCanada
        );
      } else {
        this.details = getDescription(params.data, this.translateService);
        this.fundName = fundNameGenerator(params.data, this.translateService);
        this.fundLink = this.getDownloadLinkWithPreview(params?.data);
        this.isDownloadAvailable();
      }
    }
    this.isUpdateNotificationEnabled = get(
      params,
      'isUpdateNotificationEnabled',
      false
    );
    if (this.isUpdateNotificationEnabled) {
      this.isUpdateMessageRequired();
    }
  }

  storeDefaultPage(event: MouseEvent) {
    event?.preventDefault();
    this.storageService.store<number>('ToSetPage', 1, true);
  }
  /**
   *  to set the update message whenever order date  is older than the publication date
   */
  isUpdateMessageRequired() {
    const newPublicationDateFormat = this.params.literatureData.publicationDate
      ? this.params.literatureData.publicationDate.split('-')
      : [];
    const newOrderDateFormat = this.params.literatureData.orderDate
      ? this.params.literatureData.orderDate.split('/')
      : [];

    const OrderDate = new Date(
      `20${newOrderDateFormat[1]}/${newOrderDateFormat[0]}`
    );
    const PublicationDate = new Date(
      `${newPublicationDateFormat[0]}/${newPublicationDateFormat[1]}`
    );
    if (OrderDate.getTime() < PublicationDate.getTime()) {
      this.showUpdateNotification = true;
      this.updateNotificationMessage =
        this.translateService.instant('literature.fundname-subtext-updated') +
        ` ${this.params.literatureData.orderDate} ` +
        this.translateService.instant(
          'literature.fundname-subtext-updated-message'
        );
    }
  }

  /**
   * Checks if download is available for a document based on the printOnly field and downloadUrl values
   */
  private isDownloadAvailable(): void {
    // covers scenarios for printOnly as 'Y', 'N' and undefined
    this.isShowDownload = this.params?.literatureData.downloadUrl
      ? true
      : false;
  }

  public onLinkClick(event: Event): void {
    if (this.params?.literatureData?.fundName) {
      this.trackSelectorItem(event, this.fundName, this.fundLink);
    }
  }

  private trackSelectorItem(
    event: Event,
    fundName: string,
    fundLink: string
  ): void {
    this.analyticsService.trackEvent({
      event: 'select_content',
      detailed_event: 'Select Content',
      event_data: {
        content_type: 'Literature Document',
        facets: '',
        index: '',
        link_text: fundName,
        link_url: fundLink,
        search_category: 'Literature',
        search_term: '',
      },
    });
  }

  ngOnDestroy(): void {
    this.loginSubscription?.unsubscribe();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /**
   * Get download link preview
   */
  public getDownloadLinkWithPreview(
    data: LiteratureDocumentListingData
  ): string {
    return data.literatureData?.usePreviewLink &&
      data?.literatureData?.shareclass?.previewLink &&
      data?.literatureData?.dctermsType
      ? data?.literatureData?.shareclass?.previewLink +
          '#' +
          createNavigationString(data?.literatureData?.dctermsType)
      : data?.literatureData?.pdfDownloadLink;
  }
}
